import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle 
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { 
  Plus, Save, Loader2, User, Clock, Package, AlertTriangle, CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CriarRegistoManual({ 
  open, 
  onOpenChange, 
  ofId,
  loteId,
  operacaoId,
  operadores = [],
  quantidadeDisponivel = null,
  operacaoNome = '',
  modo = 'registo', // 'registo' | 'concluir'
  gestorNome = 'Gestor',
  onSuccess 
}) {
  const [saving, setSaving] = useState(false);
  const isConcluir = modo === 'concluir';
  
  const [formData, setFormData] = useState({
    operador_id: '',
    hora_inicio: '',
    hora_fim: '',
    quantidade: '0',
    anomalias: '0',
    parcial: false,
    observacoes: ''
  });

  useEffect(() => {
    if (open) {
      const now = new Date();
      const formatDateTime = (d) => {
        const pad = (n) => String(n).padStart(2, '0');
        return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
      };
      const oneHourAgo = new Date(now.getTime() - 3600000);
      setFormData({
        operador_id: '',
        hora_inicio: formatDateTime(oneHourAgo),
        hora_fim: formatDateTime(now),
        quantidade: quantidadeDisponivel ? String(quantidadeDisponivel) : '0',
        anomalias: '0',
        parcial: false,
        observacoes: ''
      });
    }
  }, [open, quantidadeDisponivel]);

  const handleSave = async () => {
    const qtd = parseInt(formData.quantidade) || 0;
    const anomalias = parseInt(formData.anomalias) || 0;
    
    if (qtd <= 0 && !isConcluir) {
      toast.error('Indique a quantidade produzida');
      return;
    }
    
    setSaving(true);
    try {
      // Determinar nome do operador para o registo
      const operadorSelecionado = operadores.find(op => op.id === formData.operador_id);
      const nomeOperador = operadorSelecionado 
        ? (operadorSelecionado.nome_completo || operadorSelecionado.username || operadorSelecionado.nome) 
        : gestorNome;
      
      // 1. Criar registo de tempo (se houver quantidade)
      if (qtd > 0 && formData.hora_inicio && formData.hora_fim) {
        await axios.post(`${API}/registos/manual`, {
          operador_id: formData.operador_id || 'gestor',
          of_id: ofId,
          lote_id: loteId,
          operacao_id: operacaoId,
          hora_inicio: new Date(formData.hora_inicio).toISOString(),
          hora_fim: new Date(formData.hora_fim).toISOString(),
          quantidade_produzida: qtd,
          anomalias: anomalias,
          parcial: isConcluir ? false : formData.parcial,
          observacoes: formData.observacoes || (isConcluir ? `Concluída pelo gestor (${gestorNome})` : '')
        });
      }
      
      // 2. Se modo 'concluir', marcar operação como concluída
      if (isConcluir) {
        await axios.post(`${API}/operacoes/concluir-manual`, {
          of_id: ofId,
          lote_id: loteId,
          operacao_id: operacaoId,
          quantidade_produzida: qtd,
          anomalias: anomalias,
          operador_nome: nomeOperador
        });
      }
      
      // 3. Se houver anomalias, criar alerta
      if (anomalias > 0) {
        try {
          await axios.post(`${API}/alertas-anomalias`, {
            operador_id: formData.operador_id || 'gestor',
            operador_nome: nomeOperador,
            of_id: ofId,
            of_numero: '',
            lote_id: loteId,
            lote_codigo: '',
            operacao_id: operacaoId,
            operacao_nome: operacaoNome,
            quantidade_anomalias: anomalias,
            descricao: `${nomeOperador} registou ${anomalias} anomalia${anomalias !== 1 ? 's' : ''} na operação ${operacaoNome} (via gestor)`
          });
        } catch (e) {
          console.error('Erro ao criar alerta anomalias:', e.message);
        }
      }
      
      toast.success(isConcluir ? 'Operação concluída com sucesso' : 'Registo criado com sucesso');
      onSuccess && onSuccess();
      onOpenChange(false);
    } catch (error) {
      console.error('Erro:', error);
      toast.error(error?.response?.data?.detail || (isConcluir ? 'Erro ao concluir operação' : 'Erro ao criar registo'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="criar-registo-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isConcluir 
              ? <><CheckCircle className="h-5 w-5 text-green-600" /> Concluir Operação</>
              : <><Plus className="h-5 w-5 text-blue-600" /> Criar Registo Manual</>
            }
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          {/* Info: Operação e Quantidade Disponível */}
          {(operacaoNome || quantidadeDisponivel !== null) && (
            <div className="p-3 rounded-lg" style={{ background: isConcluir ? '#F0FDF4' : '#EFF6FF', border: isConcluir ? '1px solid #BBF7D0' : '1px solid #BFDBFE' }}>
              {operacaoNome && (
                <p className="text-sm font-bold" style={{ color: isConcluir ? '#166534' : '#1E40AF' }}>{operacaoNome}</p>
              )}
              {quantidadeDisponivel !== null && (
                <p className="text-sm" style={{ color: isConcluir ? '#15803D' : '#1D4ED8' }}>
                  Quantidade disponível: <strong>{quantidadeDisponivel}</strong> un.
                </p>
              )}
            </div>
          )}

          {/* Operador — opcional para gestor */}
          <div>
            <Label className="flex items-center gap-1 mb-1.5">
              <User className="h-3.5 w-3.5" /> Operador {isConcluir && <span className="text-xs text-stone-400">(opcional — predefinido: {gestorNome})</span>}
            </Label>
            <select
              className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm"
              value={formData.operador_id}
              onChange={(e) => setFormData(f => ({ ...f, operador_id: e.target.value }))}
              data-testid="select-operador-manual"
            >
              <option value="">{isConcluir ? `${gestorNome} (gestor)` : 'Selecionar operador...'}</option>
              {operadores.map(op => (
                <option key={op.id} value={op.id}>
                  {op.nome_completo || op.username || op.nome}
                </option>
              ))}
            </select>
          </div>

          {/* Horas */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="flex items-center gap-1 mb-1.5">
                <Clock className="h-3.5 w-3.5" /> Início
              </Label>
              <Input
                type="datetime-local"
                value={formData.hora_inicio}
                onChange={(e) => setFormData(f => ({ ...f, hora_inicio: e.target.value }))}
                data-testid="input-hora-inicio"
              />
            </div>
            <div>
              <Label className="flex items-center gap-1 mb-1.5">
                <Clock className="h-3.5 w-3.5" /> Fim
              </Label>
              <Input
                type="datetime-local"
                value={formData.hora_fim}
                onChange={(e) => setFormData(f => ({ ...f, hora_fim: e.target.value }))}
                data-testid="input-hora-fim"
              />
            </div>
          </div>

          {/* Quantidade e Anomalias */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="flex items-center gap-1 mb-1.5">
                <Package className="h-3.5 w-3.5" /> Quantidade
              </Label>
              <Input
                type="number"
                min="0"
                max={quantidadeDisponivel || undefined}
                value={formData.quantidade}
                onChange={(e) => setFormData(f => ({ ...f, quantidade: e.target.value }))}
                data-testid="input-quantidade-manual"
              />
            </div>
            <div>
              <Label className="flex items-center gap-1 mb-1.5">
                <AlertTriangle className="h-3.5 w-3.5" /> Anomalias
              </Label>
              <Input
                type="number"
                min="0"
                value={formData.anomalias}
                onChange={(e) => setFormData(f => ({ ...f, anomalias: e.target.value }))}
                data-testid="input-anomalias-manual"
              />
            </div>
          </div>

          {/* Parcial/Completo — só para modo registo */}
          {!isConcluir && (
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="tipo_registo"
                  checked={!formData.parcial}
                  onChange={() => setFormData(f => ({ ...f, parcial: false }))}
                  className="accent-green-600"
                  data-testid="radio-completo"
                />
                <span className="text-sm font-medium flex items-center gap-1">
                  <CheckCircle className="h-4 w-4 text-green-600" /> Completo
                </span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="tipo_registo"
                  checked={formData.parcial}
                  onChange={() => setFormData(f => ({ ...f, parcial: true }))}
                  className="accent-amber-600"
                  data-testid="radio-parcial"
                />
                <span className="text-sm font-medium flex items-center gap-1">
                  <Clock className="h-4 w-4 text-amber-600" /> Parcial
                </span>
              </label>
            </div>
          )}

          {/* Observações */}
          <div>
            <Label className="mb-1.5">Observações (opcional)</Label>
            <Input
              placeholder="Notas sobre este registo..."
              value={formData.observacoes}
              onChange={(e) => setFormData(f => ({ ...f, observacoes: e.target.value }))}
              data-testid="input-observacoes-manual"
            />
          </div>

          {/* Botão Guardar */}
          <Button
            onClick={handleSave}
            disabled={saving}
            className={`w-full ${isConcluir ? 'bg-green-600 hover:bg-green-700' : ''}`}
            data-testid="btn-guardar-registo-manual"
          >
            {saving ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> A processar...</>
            ) : isConcluir ? (
              <><CheckCircle className="h-4 w-4 mr-2" /> Concluir Operação</>
            ) : (
              <><Save className="h-4 w-4 mr-2" /> Criar Registo</>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
